import React, { useState, useEffect, useMemo } from 'react';
import { colors } from '../styles/styles';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StaggeredAnimation from '../components/common/StaggeredAnimation';

/**
 * Intentions component that displays a list of unique current intentions
 */
const Intentions = ({ thoughts }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    // Get unique current intentions from thoughts
    const currentIntentions = useMemo(() => {
        if (!thoughts || thoughts.length === 0) return [];
        
        // Filter for current thoughts with intentions
        const currentThoughts = thoughts.filter(thought => 
            thought.current === true && 
            thought.intention && 
            thought.intention.trim() !== ''
        );
        
        // Get unique intentions
        const intentionCounts = {};
        currentThoughts.forEach(thought => {
            const intention = thought.intention.trim();
            if (intentionCounts[intention]) {
                intentionCounts[intention].count += 1;
                intentionCounts[intention].thoughts.push(thought);
            } else {
                intentionCounts[intention] = {
                    name: intention,
                    count: 1,
                    thoughts: [thought]
                };
            }
        });
        
        // Convert to array and sort by count (descending) then alphabetically
        return Object.values(intentionCounts).sort((a, b) => {
            if (a.count !== b.count) {
                return b.count - a.count; // Higher count first
            }
            return a.name.localeCompare(b.name); // Then alphabetically
        });
    }, [thoughts]);

    useEffect(() => {
        // Simulate loading state
        const timer = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(timer);
    }, []);

    // Handle clicking on an intention
    const handleIntentionClick = (intention) => {
        // Navigate to home with intention filter
        navigate(`/?intention=${encodeURIComponent(intention.name)}`);
    };

    return (
        <div className="library-container smooth-scroll" style={{ width: '100%', paddingTop: '80px' }}>
            <StaggeredAnimation containerClass="intentions-feed" />
            
            <PageHeader 
                title={{ text: "Intentions", large: true }}
            />
            
            {/* Render intentions or empty state */}
            {!loading && currentIntentions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>
                    No current intentions found. Mark thoughts as current and add intentions to see them here.
                </div>
            ) : !loading && (
                <div className="intentions-feed" style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    width: '100%',
                }}>
                    {currentIntentions.map((intention, index) => (
                        <IntentionItem 
                            key={intention.name}
                            intention={intention}
                            isLast={index === currentIntentions.length - 1}
                            onClick={() => handleIntentionClick(intention)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * IntentionItem component matching CollectionItem styling exactly
 */
const IntentionItem = ({ intention, isLast, onClick }) => {
    return (
        <div 
            style={{ 
                position: 'relative', 
                overflow: 'hidden',
                willChange: 'transform, opacity',
            }}
            className="intention-item"
        >
            {/* Main content */}
            <div 
                onClick={onClick}
                style={{ 
                    padding: '16px',
                    cursor: 'pointer',
                    backgroundColor: colors.background,
                    borderBottom: isLast ? 'none' : `1px solid ${colors.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    touchAction: 'pan-y',
                }}
            >
                <span style={{
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: colors.textPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                }}>
                    <div>
                        <div style={{ marginBottom: '2px' }}>
                            {intention.name}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: colors.textSecondary,
                        }}>
                            {intention.count} thought{intention.count !== 1 ? 's' : ''}
                        </div>
                    </div>
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
        </div>
    );
};

export default Intentions; 